import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { ChevronDown, Search } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { StatusBadge } from "../components/StatusBadge";
import { CardSkeleton, EmptyState } from "../components/Skeletons";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from "../components/ui/dialog";
import { useAuth } from "../contexts/AuthContext";

const REJECT_REASONS = ["Budget exceeded", "Vendor unavailable", "Duplicate request", "Quality concern", "Other"];

export default function Approvals() {
  const { user } = useAuth();
  const [tab, setTab] = useState("pending");
  const [data, setData] = useState({ items: [] });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [expanded, setExpanded] = useState(new Set());
  const [q, setQ] = useState("");
  const [departmentId, setDepartmentId] = useState("all");
  const [procurementType, setProcurementType] = useState("all");
  const [departments, setDepartments] = useState([]);
  const [dialog, setDialog] = useState({ open: false, action: null, ids: [] });
  const [comment, setComment] = useState("");
  const [reason, setReason] = useState("");
  const [reasonText, setReasonText] = useState("");

  const canApprove = user?.role === "admin" || user?.role === "manager";

  const load = async () => {
    setLoading(true);
    const params = {
      status: tab,
      q: q || undefined,
      department_id: departmentId === "all" ? undefined : departmentId,
      procurement_type: procurementType === "all" ? undefined : procurementType,
    };
    const [r, d] = await Promise.all([
      api.get("/requests", { params }),
      departments.length ? Promise.resolve({ data: departments }) : api.get("/departments"),
    ]);
    setData(r.data);
    if (!departments.length) setDepartments(d.data);
    setSelected(new Set());
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab, departmentId, procurementType]);

  const toggle = (id) => {
    const n = new Set(selected);
    n.has(id) ? n.delete(id) : n.add(id);
    setSelected(n);
  };

  const toggleExpand = (id) => {
    const n = new Set(expanded);
    n.has(id) ? n.delete(id) : n.add(id);
    setExpanded(n);
  };

  const openDialog = (action, ids) => {
    setDialog({ open: true, action, ids });
    setComment(""); setReason(""); setReasonText("");
  };

  const submitApproval = async () => {
    try {
      const body = { request_ids: dialog.ids, action: dialog.action };
      if (dialog.action === "approve") body.comment = comment;
      else {
        if (!reason) return toast.error("Reason is required");
        body.reason = reason === "Other" ? reasonText : reason;
        if (!body.reason) return toast.error("Reason text required");
      }
      const res = await api.post("/approvals", body);
      toast.success(`${res.data.updated} request(s) ${dialog.action === "approve" ? "approved" : "rejected"}`);
      setDialog({ open: false, action: null, ids: [] });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const deptName = (id) => departments.find((d) => d.id === id)?.name || "—";

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight" style={{ fontFamily: "Outfit" }} data-testid="approvals-title">
          Approval workflow
        </h1>
        <p className="text-sm text-muted-foreground mt-1">Review pending requests and decide.</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="k-glass">
          <TabsTrigger value="pending" data-testid="tab-pending">Pending</TabsTrigger>
          <TabsTrigger value="approved" data-testid="tab-approved">Approved</TabsTrigger>
          <TabsTrigger value="rejected" data-testid="tab-rejected">Rejected</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search by brand, design, requester..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            className="pl-9 k-glass"
            data-testid="approvals-search"
          />
        </div>
        <Select value={departmentId} onValueChange={setDepartmentId}>
          <SelectTrigger className="w-44 k-glass" data-testid="approvals-dept-filter"><SelectValue placeholder="Department" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All departments</SelectItem>
            {departments.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={procurementType} onValueChange={setProcurementType}>
          <SelectTrigger className="w-32 k-glass" data-testid="approvals-type-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            <SelectItem value="new">New</SelectItem>
            <SelectItem value="refill">Refill</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {canApprove && selected.size > 0 && tab === "pending" && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          className="k-glass-strong rounded-2xl p-3 flex items-center gap-3 sticky top-20 z-20"
          data-testid="bulk-actions"
        >
          <span className="text-sm font-medium">{selected.size} selected</span>
          <div className="flex-1" />
          <Button size="sm" variant="outline" onClick={() => openDialog("reject", [...selected])} data-testid="bulk-reject">
            Reject selected
          </Button>
          <Button size="sm" className="bg-[#1D9E75] hover:bg-[#178A65]" onClick={() => openDialog("approve", [...selected])} data-testid="bulk-approve">
            Approve selected
          </Button>
        </motion.div>
      )}

      {loading ? (
        <div className="space-y-3">{Array.from({ length: 4 }).map((_, i) => <CardSkeleton key={i} />)}</div>
      ) : data.items.length === 0 ? (
        <EmptyState title={`No ${tab} requests`} description="When requests come in, they will appear here." />
      ) : (
        <div className="space-y-3">
          {data.items.map((r) => (
            <motion.div
              key={r.id}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              className="k-glass rounded-2xl overflow-hidden"
              data-testid={`approval-row-${r.id}`}
            >
              <div className="p-4 flex flex-wrap items-center gap-4">
                {canApprove && tab === "pending" && (
                  <Checkbox
                    checked={selected.has(r.id)}
                    onCheckedChange={() => toggle(r.id)}
                    data-testid={`select-${r.id}`}
                  />
                )}
                <div className="w-9 h-9 rounded-full bg-[#534AB7]/15 text-[#534AB7] flex items-center justify-center text-xs font-semibold">
                  {(r.requester_name || "?").slice(0, 2).toUpperCase()}
                </div>
                <div className="flex-1 min-w-[160px]">
                  <div className="text-sm font-semibold">{r.brand || "—"} · {r.design}</div>
                  <div className="text-xs text-muted-foreground">{r.requester_name} • {deptName(r.department_id)}</div>
                </div>
                <span className="hidden sm:inline-block text-xs px-2 py-0.5 rounded-full bg-[#534AB7]/10 text-[#534AB7]">
                  {r.procurement_type === "new" ? "New" : "Refill"}
                </span>
                <div className="text-right">
                  <div className="text-lg font-bold" style={{ fontFamily: "Outfit", color: "#534AB7" }}>{r.total_qty}</div>
                  <div className="text-[10px] text-muted-foreground">units</div>
                </div>
                <StatusBadge status={r.status} />
                <button
                  onClick={() => toggleExpand(r.id)}
                  className="p-2 rounded-lg hover:bg-foreground/5"
                  data-testid={`expand-${r.id}`}
                >
                  <ChevronDown size={16} className={`transition-transform ${expanded.has(r.id) ? "rotate-180" : ""}`} />
                </button>
              </div>
              <AnimatePresence>
                {expanded.has(r.id) && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="border-t border-border/60"
                  >
                    <div className="p-4 grid sm:grid-cols-3 gap-4 text-sm">
                      <div>
                        <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Details</div>
                        <div>Color: {r.color}</div>
                        <div>Fabric: {r.fabric}</div>
                        <div>Pattern: {r.pattern}</div>
                        <div>City: {r.city}</div>
                      </div>
                      <div className="sm:col-span-2">
                        <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Size breakdown</div>
                        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                          {r.sizes?.map((s) => (
                            <div key={s.size} className="p-2 rounded-lg bg-foreground/5 text-center">
                              <div className="text-xs text-muted-foreground">{s.size}</div>
                              <div className="font-semibold">{s.quantity}</div>
                            </div>
                          ))}
                        </div>
                        {r.approval_comment && <div className="text-xs mt-2"><b>Comment:</b> {r.approval_comment}</div>}
                        {r.rejection_reason && <div className="text-xs mt-2"><b>Reason:</b> {r.rejection_reason}</div>}
                      </div>
                    </div>
                    {canApprove && tab === "pending" && (
                      <div className="px-4 pb-4 flex gap-2 justify-end">
                        <Button size="sm" variant="outline" onClick={() => openDialog("reject", [r.id])} data-testid={`reject-${r.id}`}>
                          Reject
                        </Button>
                        <Button size="sm" className="bg-[#1D9E75] hover:bg-[#178A65]" onClick={() => openDialog("approve", [r.id])} data-testid={`approve-${r.id}`}>
                          Approve
                        </Button>
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      )}

      <Dialog open={dialog.open} onOpenChange={(o) => !o && setDialog({ open: false, action: null, ids: [] })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {dialog.action === "approve" ? "Approve" : "Reject"} {dialog.ids.length} request{dialog.ids.length > 1 ? "s" : ""}
            </DialogTitle>
            <DialogDescription>
              {dialog.action === "reject" ? "Please provide a reason. This is required." : "Add an optional comment."}
            </DialogDescription>
          </DialogHeader>
          {dialog.action === "approve" ? (
            <Textarea value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Optional comment..." data-testid="approval-comment" />
          ) : (
            <div className="space-y-3">
              <Select value={reason} onValueChange={setReason}>
                <SelectTrigger data-testid="reject-reason"><SelectValue placeholder="Select a reason" /></SelectTrigger>
                <SelectContent>
                  {REJECT_REASONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
              {reason === "Other" && (
                <Textarea value={reasonText} onChange={(e) => setReasonText(e.target.value)} placeholder="Describe..." data-testid="reject-reason-text" />
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialog({ open: false, action: null, ids: [] })}>Cancel</Button>
            <Button
              onClick={submitApproval}
              className={dialog.action === "approve" ? "bg-[#1D9E75] hover:bg-[#178A65]" : "bg-red-600 hover:bg-red-700"}
              data-testid="confirm-approval"
            >
              Confirm {dialog.action}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
